import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { networkManager, PlayerState } from '../network/NetworkManager';
import { BasicFart } from '../entities/BasicFart';
import { LingeringFart } from '../entities/farts/LingeringFart';
import { GhostFart } from '../entities/farts/GhostFart';
import { ExplosiveFart } from '../entities/farts/ExplosiveFart';
import { SBDFart } from '../entities/farts/SBDFart';
import { FartType } from '../entities/farts/FartTypes';
import { TileType } from './MazeGenerator';
import { TILE_SIZE, GAME_WIDTH, GAME_HEIGHT } from '../config/game.config';
import { GameStateManager } from './GameStateManager';
import { isDebugMode } from '../utils/DebugConfig';
import { calculateFartParams } from '../utils/FartCalculations';
// Removed snapshot-interpolation library - it has timing issues in P2P (non-client-server) contexts
// Instead we use simple linear interpolation toward the target position each frame

// Interpolation factor (0-1): higher = faster catch-up, lower = smoother but more delay
// 0.15 means move 15% of the remaining distance each frame at 60fps
const INTERPOLATION_FACTOR = 0.2;

export class MultiplayerSync {
    private scene: Phaser.Scene;
    private player!: Player;
    private remotePlayers: Map<string, Player>;
    private walls: Phaser.Physics.Arcade.StaticGroup;
    private farts: Phaser.Physics.Arcade.Group;
    private mazeData: TileType[][];
    private gameStateManager?: GameStateManager;

    private syncTimer: number = 0;

    // Delta sync configuration (Phase 1: constants, future: from host config)
    private readonly SYNC_RATE_ACTIVE = 50;      // ms minimum between sends when moving
    private readonly SYNC_RATE_URGENT = 20;      // ms minimum for urgent updates (start/stop/state change)
    private readonly HEARTBEAT_RATE = 500;       // ms between heartbeats when stationary
    private readonly MOVING_HEARTBEAT = 150;      // ms between redundant sends when moving (packet loss protection)
    private readonly VELOCITY_THRESHOLD = 30;     // velocity change to trigger send
    private readonly POSITION_THRESHOLD = 15;     // predicted position drift to trigger send

    // Last sent state for delta detection
    private lastSentState: {
        x: number;
        y: number;
        vx: number;
        vy: number;
        health: number;
        capacity: number;
        isCharging: boolean;
        isStunned: boolean;
        timestamp: number;
    } | null = null;

    private remotePlayersGroup: Phaser.GameObjects.Group;
    private firstPacketReceived: Map<string, boolean> = new Map();

    // Track lingering farts for update loop
    private lingeringFarts: LingeringFart[] = [];

    // Store target positions and state for each remote player
    // We smoothly interpolate the visual position toward the target each frame
    private remotePlayerStates: Map<string, {
        targetX: number;    // Target position from network (interpolate toward this)
        targetY: number;
        health: number;
        capacity: number;
        color: number;
        isCharging: boolean;
        isStunned: boolean;
    }> = new Map();

    constructor(
        scene: Phaser.Scene,
        remotePlayers: Map<string, Player>,
        walls: Phaser.Physics.Arcade.StaticGroup,
        farts: Phaser.Physics.Arcade.Group,
        mazeData: TileType[][]
    ) {
        this.scene = scene;
        this.remotePlayers = remotePlayers;
        this.walls = walls;
        this.farts = farts;
        this.mazeData = mazeData;
        this.remotePlayersGroup = this.scene.add.group();
        // Simple linear interpolation is used instead of snapshot-interpolation library
    }

    public setGameStateManager(gameStateManager: GameStateManager): void {
        this.gameStateManager = gameStateManager;
    }

    public setPlayer(player: Player): void {
        this.player = player;
    }

    public setup(): void {
        // Check if debug mode is enabled
        const isDebug = isDebugMode();

        // Listen for position updates from other players
        networkManager.subscribe('PLAYER_POSITION', (message) => {
            const state = message.payload as PlayerState;

            // Debug logging when in debug mode
            if (isDebug) {
                console.log(`[DEBUG SYNC] Received PLAYER_POSITION from ${state.id?.slice(-4)}: pos=(${Math.round(state.x)},${Math.round(state.y)}) health=${state.health} stunned=${state.isStunned}`);
            }

            // Update the remote player target position (interpolation happens in update())
            this.updateRemotePlayer(state);
        });

        // Listen for batch updates (from Host Relay)
        networkManager.subscribe('REMOTE_STATES_SYNC', (message) => {
            const states = message.payload as PlayerState[];
            states.forEach(state => {
                if (state.id !== networkManager.playerId) {
                    this.updateRemotePlayer(state);
                }
            });
        });

        // Legacy FART_RELEASE listener removed - replaced by CHARGED_FART system

        // Listen for charge events
        networkManager.subscribe('CHARGE_START', (message) => {
            const { playerId } = message.payload as { playerId: string };
            if (playerId === networkManager.playerId) return;
            const remote = this.remotePlayers.get(playerId);
            if (remote) remote.startCharging();

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast('CHARGE_START', message.payload);
            }
        });

        networkManager.subscribe('CHARGED_FART', (message) => {
            const { x, y, chargePercent, ownerId, fartType, direction } = message.payload as {
                x: number;
                y: number;
                chargePercent: number;
                ownerId: string;
                fartType?: FartType;
                direction?: { x: number; y: number };
            };
            if (ownerId === networkManager.playerId) return;

            const remote = this.remotePlayers.get(ownerId);
            if (remote) remote.stopCharging();

            // Create appropriate fart type
            const type = fartType || FartType.BASIC;

            switch (type) {
                case FartType.LINGERING:
                    const lingeringFart = new LingeringFart(this.scene, x, y, {
                        ownerId: ownerId,
                    });
                    this.farts.add(lingeringFart);
                    this.lingeringFarts.push(lingeringFart);
                    break;

                case FartType.GHOST: {
                    // GhostFart gets damage/lifespan from FART_CONFIGS internally
                    const ghostFart = new GhostFart(this.scene, x, y, {
                        ownerId: ownerId,
                    });
                    this.farts.add(ghostFart);
                    break;
                }

                case FartType.EXPLOSIVE: {
                    // ExplosiveFart gets damage/radius from FART_CONFIGS internally
                    const explosiveFart = new ExplosiveFart(this.scene, x, y, {
                        ownerId: ownerId,
                    });
                    this.farts.add(explosiveFart);
                    break;
                }

                case FartType.SBD: {
                    // SBDFart requires direction - use provided or default to right
                    const sbdDirection = direction || { x: 1, y: 0 };
                    const sbdFart = new SBDFart(this.scene, x, y, {
                        ownerId: ownerId,
                        direction: sbdDirection,
                        mazeData: this.mazeData,
                    });
                    this.farts.add(sbdFart);
                    break;
                }

                case FartType.PROPULSION:
                    // Propulsion farts don't create projectiles - they just move the player
                    // No entity needed for remote display
                    break;

                case FartType.BASIC:
                default: {
                    const params = calculateFartParams(chargePercent);
                    const fart = new BasicFart(this.scene, x, y, params);
                    fart.setData('ownerId', ownerId);
                    fart.setData('chargePercent', chargePercent);
                    fart.setDepth(40);
                    this.farts.add(fart);
                    break;
                }
            }

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast('CHARGED_FART', message.payload);
            }
        });

        networkManager.subscribe('FART_CANCEL', (message) => {
            const { playerId } = message.payload as { playerId: string };
            const remote = this.remotePlayers.get(playerId);
            if (remote) remote.stopCharging();

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast('FART_CANCEL', message.payload);
            }
        });

        // Listen for victory message
        networkManager.subscribe('victory', (message) => {
            if (this.gameStateManager && !this.gameStateManager.isGameOver) {
                this.gameStateManager.showGameOver(false); // Someone else won
            }

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast('victory', message.payload);
            }
        });

        // Create initial remote player sprites
        const peerIds = networkManager.peerIds;
        if (isDebug) {
            console.log(`[DEBUG SYNC] Setup: Found ${peerIds.length} peers: ${peerIds.map(p => p.slice(-4)).join(', ')}`);
            console.log(`[DEBUG SYNC] My playerId: ${networkManager.playerId}`);
        }
        peerIds.forEach((peerId) => {
            this.createRemotePlayer(peerId);
        });

        networkManager.onPeerJoin = (peerId) => {
            if (isDebug) console.log(`[DEBUG SYNC] onPeerJoin: ${peerId.slice(-4)}`);
            this.createRemotePlayer(peerId);
        };

        networkManager.onPeerLeave = (peerId) => {
            this.removeRemotePlayer(peerId);
        };
    }

    public destroy(): void {
        networkManager.unsubscribe('PLAYER_POSITION');
        networkManager.unsubscribe('REMOTE_STATES_SYNC');
        // FART_RELEASE removed - using CHARGED_FART only
        networkManager.unsubscribe('CHARGE_START');
        networkManager.unsubscribe('CHARGED_FART');
        networkManager.unsubscribe('FART_CANCEL');
        networkManager.unsubscribe('victory');

        networkManager.onPeerJoin = undefined;
        networkManager.onPeerLeave = undefined;



        // Clean up sprites and state caches
        this.remotePlayersGroup.clear(true, true);
        this.remotePlayers.clear();
        this.remotePlayerStates.clear();
        this.firstPacketReceived.clear();
    }

    public update(delta: number): void {
        this.syncTimer += delta;

        // Smart sync: only send when needed
        if (this.shouldSendPositionUpdate()) {
            this.syncTimer = 0;
            this.broadcastPosition();

            // If Host, relay all remote positions to all other clients
            // This fixes the mesh connectivity issue where clients only see the host
            if (networkManager.isHost && networkManager.peerIds.length > 1) {
                this.relayRemotePositions();
            }
        }

        // Update lingering farts and remove destroyed ones
        this.lingeringFarts = this.lingeringFarts.filter(fart => {
            if (fart.active) {
                fart.update(this.scene.time.now, delta);
                return true;
            }
            return false;
        });

        // --- SIMPLE LINEAR INTERPOLATION ---
        // Smoothly move each remote player toward their target position
        // This is simpler and more reliable than snapshot-interpolation in P2P contexts
        const now = this.scene.time.now;

        this.remotePlayers.forEach((remote, playerId) => {
            if (!remote.visible) return;

            const cachedState = this.remotePlayerStates.get(playerId);
            if (!cachedState) return;

            // Lerp toward target position
            const dx = cachedState.targetX - remote.x;
            const dy = cachedState.targetY - remote.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // If very close, snap to target; otherwise lerp
            if (distance < 1) {
                remote.x = cachedState.targetX;
                remote.y = cachedState.targetY;
            } else if (distance > 200) {
                // Teleport if too far (respawn, etc.)
                remote.x = cachedState.targetX;
                remote.y = cachedState.targetY;
            } else {
                // Smooth interpolation toward target
                remote.x += dx * INTERPOLATION_FACTOR;
                remote.y += dy * INTERPOLATION_FACTOR;
            }

            // Update label position
            const label = this.scene.children.getByName(`label_${playerId}`) as Phaser.GameObjects.Text;
            if (label) {
                label.x = remote.x;
                label.y = remote.y - 20;
            }

            // Update charging base position for visual effects
            if (remote.isCharging) {
                remote.updateChargeBasePosition(remote.x, remote.y);
            }

            // Apply non-interpolated state
            remote.health = cachedState.health;

            // Sync charging state
            if (cachedState.isCharging && !remote.isCharging) {
                remote.startCharging();
            } else if (!cachedState.isCharging && remote.isCharging) {
                remote.stopCharging();
            }

            // Sync stun state
            if (cachedState.isStunned && !remote.isStunned) {
                remote.stun(1000, false);
            } else if (!cachedState.isStunned && remote.isStunned) {
                remote.clearStun();
            }

            remote.update(now, delta);
        });
    }

    /**
     * Determine if we should send a position update based on delta detection
     */
    private shouldSendPositionUpdate(): boolean {
        if (!this.player) return false;

        // First update - always send
        if (!this.lastSentState) return true;

        const now = Date.now();
        const timeSinceLastSend = now - this.lastSentState.timestamp;

        // 1. ABSOLUTE THROTTLE (20ms)
        // Never send faster than 50hz to prevent flooding
        if (timeSinceLastSend < this.SYNC_RATE_URGENT) return false;

        const currentX = this.player.body ? this.player.body.x + (this.player.body.width / 2) : this.player.x;
        const currentY = this.player.body ? this.player.body.y + (this.player.body.height / 2) : this.player.y;
        const currentVx = this.player.body?.velocity.x ?? 0;
        const currentVy = this.player.body?.velocity.y ?? 0;

        // --- URGENT TRIGGERS ---

        // Movement state changed (Start/Stop)
        const isMoving = Math.abs(currentVx) > 5 || Math.abs(currentVy) > 5;
        const wasMoving = Math.abs(this.lastSentState.vx) > 5 || Math.abs(this.lastSentState.vy) > 5;
        if (isMoving !== wasMoving) return true;

        // Critical state changes
        const stateChanged =
            this.player.health !== this.lastSentState.health ||
            this.player.capacity !== this.lastSentState.capacity ||
            this.player.isCharging !== this.lastSentState.isCharging ||
            this.player.isStunned !== this.lastSentState.isStunned;
        if (stateChanged) return true;

        // Sudden velocity spike
        const velocityDelta = Math.sqrt(
            Math.pow(currentVx - this.lastSentState.vx, 2) +
            Math.pow(currentVy - this.lastSentState.vy, 2)
        );
        if (velocityDelta > this.VELOCITY_THRESHOLD) return true;

        // --- MAINTENANCE TRIGGERS ---

        // Moving Heartbeat: Redundancy for packet loss (150ms)
        if (isMoving && timeSinceLastSend >= this.MOVING_HEARTBEAT) return true;

        // Position Drift Check (every 50ms)
        if (timeSinceLastSend >= this.SYNC_RATE_ACTIVE) {
            const elapsedSeconds = timeSinceLastSend / 1000;
            const predictedX = this.lastSentState.x + (this.lastSentState.vx * elapsedSeconds);
            const predictedY = this.lastSentState.y + (this.lastSentState.vy * elapsedSeconds);
            const positionDrift = Math.sqrt(
                Math.pow(currentX - predictedX, 2) +
                Math.pow(currentY - predictedY, 2)
            );
            if (positionDrift > this.POSITION_THRESHOLD) return true;
        }

        // Stationary Heartbeat (500ms)
        if (!isMoving && timeSinceLastSend >= this.HEARTBEAT_RATE) return true;

        return false;
    }

    public broadcastPosition(): void {
        if (!this.player) return;

        const x = this.player.body ? this.player.body.x + (this.player.body.width / 2) : this.player.x;
        const y = this.player.body ? this.player.body.y + (this.player.body.height / 2) : this.player.y;
        const vx = this.player.body?.velocity.x ?? 0;
        const vy = this.player.body?.velocity.y ?? 0;

        const state: PlayerState = {
            id: networkManager.playerId,
            x, y, vx, vy,
            health: this.player.health,
            capacity: this.player.capacity,
            color: this.player.color,
            isCharging: this.player.isCharging,
            isStunned: this.player.isStunned,
        };

        // Update last sent state for delta detection
        this.lastSentState = {
            x, y, vx, vy,
            health: this.player.health,
            capacity: this.player.capacity,
            isCharging: this.player.isCharging,
            isStunned: this.player.isStunned,
            timestamp: Date.now(),
        };

        // Use unreliable for frequent position updates
        networkManager.broadcast('PLAYER_POSITION', state);
    }

    private relayRemotePositions(): void {
        // Collect all remote player states
        const remoteStates: PlayerState[] = [];
        this.remotePlayers.forEach((p, id) => {
            remoteStates.push({
                id: id,
                x: p.x,
                y: p.y,
                vx: p.body?.velocity.x ?? 0,
                vy: p.body?.velocity.y ?? 0,
                health: p.health,
                capacity: p.getData('capacity') || 0,
                color: p.color,
                isCharging: p.isCharging,
                isStunned: p.isStunned,
            });
        });

        if (remoteStates.length > 0) {
            networkManager.broadcast('REMOTE_STATES_SYNC', remoteStates);
        }
    }


    // broadcastFart removed - use broadcastChargedFart instead

    public broadcastChargeStart(): void {
        networkManager.broadcast('CHARGE_START', {
            playerId: networkManager.playerId,
        });
    }

    public broadcastChargedFart(x: number, y: number, chargePercent: number, fartType: FartType = FartType.BASIC, direction?: { x: number; y: number }): void {
        networkManager.broadcast('CHARGED_FART', {
            x,
            y,
            chargePercent,
            ownerId: networkManager.playerId,
            fartType,
            direction,
        });
    }

    public getMySpawnIndex(): number {
        // Determine spawn index based on peer ID ordering
        const allPeers = [networkManager.playerId, ...networkManager.peerIds].sort();
        const myIndex = allPeers.indexOf(networkManager.playerId);

        console.log(`[SPAWN] My ID: ${networkManager.playerId?.slice(-6)}`);
        console.log(`[SPAWN] All peers: ${allPeers.map(p => p.slice(-6)).join(', ')}`);
        console.log(`[SPAWN] My spawn index: ${myIndex}`);

        return myIndex;
    }

    public findAllSpawnPoints(): { x: number; y: number }[] {
        const spawns: { x: number; y: number }[] = [];

        for (let y = 0; y < this.mazeData.length; y++) {
            for (let x = 0; x < this.mazeData[y].length; x++) {
                if (this.mazeData[y][x] === TileType.SPAWN) {
                    spawns.push({
                        x: x * TILE_SIZE + TILE_SIZE / 2,
                        y: y * TILE_SIZE + TILE_SIZE / 2,
                    });
                }
            }
        }

        // Sort spawns for deterministic order (top-left to bottom-right)
        spawns.sort((a, b) => {
            if (a.y !== b.y) return a.y - b.y;
            return a.x - b.x;
        });

        // Fallback if no spawn points
        if (spawns.length === 0) {
            spawns.push({ x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 });
        }

        console.log(`[SPAWN] Found ${spawns.length} spawn points`);
        return spawns;
    }

    private createRemotePlayer(peerId: string): void {
        if (this.remotePlayers.has(peerId)) return;

        const isDebug = isDebugMode();

        const remote = new Player(this.scene, 0, 0, 'player');

        // Remote players start hidden until we receive their first position packet
        remote.setVisible(false);
        remote.setDisplaySize(28, 28);
        remote.setDepth(50);
        remote.setTint(0xffffff);

        if (isDebug) {
            console.log(`[DEBUG SYNC] Created remote player for ${peerId.slice(-4)} (hidden until first packet)`);
        }

        // Name label
        const label = this.scene.add.text(0, -20, peerId.slice(-4), {
            fontSize: '10px',
            color: '#ffffff',
            backgroundColor: '#00000080',
        });
        label.setOrigin(0.5);
        label.setName(`label_${peerId}`);
        label.setVisible(false);
        label.setDepth(1000); // High depth

        this.remotePlayers.set(peerId, remote);
        this.remotePlayersGroup.add(remote);
        this.remotePlayersGroup.add(label);

        if (remote.body) {
            this.scene.physics.world.disableBody(remote.body);
            (remote as any).body = undefined;
        }

        if (isDebug) {
            console.log(`[DEBUG SYNC] Remote player created: visible=${remote.visible}, x=${remote.x}, y=${remote.y}, depth=${remote.depth}, displayWidth=${remote.displayWidth}`);
        }
    }

    private updateRemotePlayer(state: PlayerState): void {
        const isDebug = isDebugMode();

        let remote = this.remotePlayers.get(state.id);

        if (!remote) {
            if (isDebug) console.log(`[DEBUG SYNC] Creating new remote player for ${state.id?.slice(-4)} - Map size before: ${this.remotePlayers.size}`);
            this.createRemotePlayer(state.id);
            remote = this.remotePlayers.get(state.id);
            if (isDebug) console.log(`[DEBUG SYNC] After creation: remote=${!!remote}, Map size: ${this.remotePlayers.size}, Map keys: ${Array.from(this.remotePlayers.keys()).map(k => k?.slice(-4)).join(',')}`);
        }

        if (remote) {
            const isDead = state.health <= 0;
            const isFirstPacket = !this.firstPacketReceived.get(state.id);

            if (isDebug && isFirstPacket) {
                console.log(`[DEBUG SYNC] FIRST PACKET for ${state.id?.slice(-4)}: visible=${remote.visible}, pos=(${state.x}, ${state.y})`);
            }

            // Store the target position and state for interpolation in update()
            this.remotePlayerStates.set(state.id, {
                targetX: state.x,  // Target position to interpolate toward
                targetY: state.y,
                health: state.health,
                capacity: state.capacity,
                color: state.color ?? remote.color,
                isCharging: state.isCharging ?? false,
                isStunned: state.isStunned ?? false,
            });

            // Apply color tint
            if (state.color !== undefined && !remote.isStunned) {
                remote.setTint(state.color);
                const label = this.scene.children.getByName(`label_${state.id}`) as Phaser.GameObjects.Text;
                if (label) label.setBackgroundColor('#' + state.color.toString(16).padStart(6, '0'));
            }

            if (isFirstPacket) {
                // First packet - teleport to position immediately (no interpolation for first position)
                remote.setPosition(state.x, state.y);
                remote.updateChargeBasePosition(state.x, state.y);
                this.firstPacketReceived.set(state.id, true);

                if (isDebug) {
                    console.log(`[DEBUG SYNC] First packet from ${state.id?.slice(-4)} at (${state.x}, ${state.y})`);
                }
            }
            // For subsequent updates, the simple lerp in update() will handle smooth movement

            // Visibility handling
            if (!isDead) {
                remote.setVisible(true);
                const label = this.scene.children.getByName(`label_${state.id}`) as Phaser.GameObjects.Text;
                if (label) {
                    label.setVisible(true);
                    if (isFirstPacket) {
                        label.setPosition(remote.x, remote.y - 25);
                    }
                }
            } else {
                remote.setVisible(false);
                const label = this.scene.children.getByName(`label_${state.id}`) as Phaser.GameObjects.Text;
                if (label) label.setVisible(false);
            }
        }
    }

    private removeRemotePlayer(peerId: string): void {
        const remote = this.remotePlayers.get(peerId);
        if (remote) {
            remote.destroy();
            this.remotePlayers.delete(peerId);
            this.remotePlayerStates.delete(peerId);
            this.firstPacketReceived.delete(peerId);

            const label = this.scene.children.getByName(`label_${peerId}`);
            if (label) label.destroy();
        }
    }

    // showRemoteFart removed - CHARGED_FART handler creates farts directly
}
